import { SafeError, SafePromise, SafeType } from '@benzinga/safe-await';
import { commaSeparatedSymbols } from '@benzinga/utils';
import { ExtendedSubscribable, Subscription } from '@benzinga/subscribable';
import {
  Logo,
  Quote,
  QuoteDetail,
  Schedule,
  Security,
  TickerDetailsResponse,
  ShortInterestParams,
  ShortInterestsDataSet,
  DetailedQuotesBySymbol,
  GetQuotesLogoParams,
  DelayedQuote,
  TickerDetail,
  BullBearStatements,
  QuoteAnalysisResponse,
  PeerQuotesResponse,
} from './entities';
import { QuoteSocketEvent, QuoteSocket } from './socket';
import { QuoteFeedEvent, QuoteFeed, QuoteFeedExtended } from './feed';
import { QuoteStore, REJECTED_LOGO_TTL_MS, getPeerQuotesCacheKey } from './store';
import { Session } from '@benzinga/session';
import { StockSymbol } from '@benzinga/session';
import { WatchlistQuotesRequest, WatchlistQuotesRequestEvent } from './request';
import { QuoteFeedRestful, QuoteFeedRestfulEvent } from './feedRestful';
import { createSymbolRequestCoalescer } from './symbolRequestCoalesce';

type QuotesLogoResult =
  | { err?: undefined; ok: Logo[]; rejectedSymbols: StockSymbol[] }
  | { err: SafeError; ok?: undefined };

const logoRequestRejectedMessage = '400 Oops! looks like something went wrong.';

const readLogoHttpStatus = (err: SafeError): number | undefined => {
  const fromData = (err.data as { status?: unknown } | undefined)?.status;
  if (typeof fromData === 'number') return fromData;
  const fromMessage = Number.parseInt(err.message, 10);
  return Number.isNaN(fromMessage) ? undefined : fromMessage;
};

interface WatchlistQuotesFunctions {
  getDetailedQuotes: QuotesManager['getDetailedQuotes'];
}

interface WatchlistUpdateEvent {
  quotes: DetailedQuotesBySymbol;
  type: 'quote:detailed_quotes';
}

interface TickerDetailEvent {
  detail: TickerDetail[];
  type: 'quote:ticker_details_quotes';
}
/**
 * Event fired when short interest quotes were updated
 *
 * @event
 *
 * @interface ShortInterestUpdateEvent
 */
interface ShortInterestUpdateEvent {
  quotes: ShortInterestsDataSet;
  type: 'quote:short_interest_quotes';
}

export type QuotesManagerEvent =
  | QuoteSocketEvent
  | QuoteFeedEvent
  | QuoteFeedRestfulEvent
  | WatchlistQuotesRequestEvent
  | WatchlistUpdateEvent
  | ShortInterestUpdateEvent
  | TickerDetailEvent;

/**
 * Quotes manager
 *
 * Main class for working with Quotes API
 *
 * To use, obtain an instance by calling session.getManager(QuotesManager)
 *
 * @export
 * @class QuotesManager
 * @extends {ExtendedSubscribable<QuotesManagerEvent, WatchlistQuotesFunctions>}
 */
export class QuotesManager extends ExtendedSubscribable<QuotesManagerEvent, WatchlistQuotesFunctions> {
  private socket: QuoteSocket;
  private socketSubscription?: Subscription<QuoteSocket>;

  private store: QuoteStore;
  private request: WatchlistQuotesRequest;
  private requestSubscription?: Subscription<WatchlistQuotesRequest>;

  private feedRestful: QuoteFeedRestful;
  private feedRestfulSubscription?: Subscription<QuoteFeedRestful>;

  private delayedQuotesCoalescer: ReturnType<
    typeof createSymbolRequestCoalescer<SafeType<Record<StockSymbol, DelayedQuote>>>
  >;
  private tickerDetailsCoalescer: ReturnType<typeof createSymbolRequestCoalescer<SafeType<TickerDetailsResponse>>>;
  private quotesLogosCoalescers = new Map<
    string,
    ReturnType<typeof createSymbolRequestCoalescer<QuotesLogoResult>>
  >();
  private rejectedLogoSymbols = new Map<string, number>();

  constructor(session: Session) {
    super();
    this.socket = new QuoteSocket(session);
    this.store = new QuoteStore();
    this.request = new WatchlistQuotesRequest(session);
    this.feedRestful = new QuoteFeedRestful(session, this.store);
    this.delayedQuotesCoalescer = createSymbolRequestCoalescer({
      fetch: symbols => this.fetchDelayedQuotes(symbols),
      slice: (result, symbols) => this.sliceDelayedQuotesResult(result, symbols),
    });
    this.tickerDetailsCoalescer = createSymbolRequestCoalescer({
      fetch: symbols => this.fetchTickerDetails(symbols),
      slice: (result, symbols) => this.sliceTickerDetailsResult(result, symbols),
    });
  }

  /**
   * @internal
   */
  public static getName = () => 'benzinga-quotes';

  /**
   * Do not use store directly! Use methods in the manger itself to query data
   *
   * @internal
   */
  public getStore(): QuoteStore {
    return this.store;
  }

  /**
   * Get quote for given security from cache (if exists)
   *
   * @param {Security} security
   * @memberof QuotesManager
   */
  public getCachedQuote = (security: Security): Quote | undefined => {
    const quoteFeed = this.store.getQuoteFeed(security);

    if (quoteFeed) {
      return quoteFeed.getQuote();
    } else {
      return undefined;
    }
  };

  /**
   * Get quote detail for given security from cache (if exists)
   *
   * @param {Security} security
   * @memberof QuotesManager
   */
  public getCachedQuoteDetail = (security: Security): QuoteDetail | undefined => {
    const quoteFeed = this.store.getQuoteFeed(security);

    if (quoteFeed) {
      return quoteFeed.getDetail();
    } else {
      return undefined;
    }
  };

  /**
   * Create a feed for given quote security
   *
   * For each security there is only one feed created, so multiple calls will return the same instance
   *
   * @param {Security} security
   * @memberof QuotesManager
   */
  public createQuoteFeed = (security: Security): QuoteFeed => {
    const quoteFeed = this.store.getQuoteFeed(security);
    if (quoteFeed) {
      return quoteFeed;
    } else {
      return this.store.addQuoteFeed(new QuoteFeedExtended(this.socket.createSession(security), this.store));
    }
  };

  /**
   * Get detailed quotes for given symbols
   *
   * Note that this method wil use cache as much as possible
   *
   * @param {StockSymbol[]} symbols
   * @memberof QuotesManager
   */
  public getDetailedQuotes = async (symbols: StockSymbol[]): SafePromise<DetailedQuotesBySymbol> => {
    let numberOfCachedQuotes = 0;
    const cachedQuotes = symbols.reduce((acc, symbol) => {
      const detailedQuote = this.store.getDetailedQuotes(symbol);
      if (detailedQuote) {
        numberOfCachedQuotes += 1;
        return { ...acc, [symbol]: detailedQuote };
      } else {
        return acc;
      }
    }, {});

    if (numberOfCachedQuotes === symbols.length) {
      return { ok: cachedQuotes };
    } else {
      const watchlistQuotes = await this.request.getDetailedQuotes(symbols);
      if (watchlistQuotes.err) {
        return watchlistQuotes;
      } else {
        Object.entries(watchlistQuotes.ok).forEach(([key, value]) => this.store.setDetailedQuotes(key, value));
        this.dispatch({
          quotes: watchlistQuotes.ok,
          type: 'quote:detailed_quotes',
        });
        return { ok: { ...cachedQuotes, ...watchlistQuotes.ok } };
      }
    }
  };

  /**
   * Get quotes logo for given symbols and params
   *
   * Will use internal cache when possible
   *
   * @param {StockSymbol[]} symbols
   * @param {GetQuotesLogoParams} [params]
   * @memberof QuotesManager
   */
  public getQuotesLogos = async (symbols: StockSymbol[], params?: GetQuotesLogoParams): Promise<QuotesLogoResult> => {
    const uniqueSymbols = this.getUniqueSymbols(symbols);
    const cachedLogos = this.getQuotesLogosCached(uniqueSymbols, params);

    if (cachedLogos.length === uniqueSymbols.length) {
      return { ok: cachedLogos, rejectedSymbols: [] };
    }

    const missingSymbols = this.getUniqueMissingLogoSymbols(uniqueSymbols, params);
    const { freshSymbols, rejectedSymbols } = this.partitionRejectedLogoSymbols(missingSymbols);
    if (freshSymbols.length === 0) {
      return { ok: cachedLogos, rejectedSymbols };
    }

    const response = await this.getQuotesLogosCoalescer(params).request(freshSymbols);
    if (response.err) {
      return { err: response.err };
    }

    return {
      ok: this.dedupeLogosBySearchKey(cachedLogos, response.ok),
      rejectedSymbols: [...rejectedSymbols, ...response.rejectedSymbols],
    };
  };

  public getQuotesLogosMapped = async (
    symbols: StockSymbol[],
    params?: GetQuotesLogoParams,
  ): SafePromise<Record<StockSymbol, Logo>> => {
    const cachedLogos = this.getQuotesLogosMappedCached(symbols, params);

    if (Object.keys(cachedLogos).length === symbols.length) {
      return { ok: cachedLogos };
    }

    const quotesLogos = await this.getQuotesLogos(symbols, params);
    if (quotesLogos.err) {
      return { err: quotesLogos.err };
    }

    return { ok: this.mapQuotesLogos(quotesLogos.ok) };
  };

  public getQuotesLogosCached = (symbols: StockSymbol[], params?: GetQuotesLogoParams): Logo[] => {
    return symbols.reduce((acc, symbol) => {
      const quotesLogo = this.store.getQuotesLogos(symbol, params);
      if (quotesLogo) {
        return [...acc, quotesLogo];
      } else {
        return acc;
      }
    }, [] as Logo[]);
  };

  public getQuotesLogosMappedCached = (
    symbols: StockSymbol[],
    params?: GetQuotesLogoParams,
  ): Record<StockSymbol, Logo> => {
    const logos = {};

    if (Array.isArray(symbols)) {
      symbols?.forEach(symbol => {
        const quotesLogo = this.store.getQuotesLogos(symbol, params);
        if (quotesLogo) {
          logos[symbol] = quotesLogo;
        }
      });
    }

    return logos;
  };

  public getQuotesLogoCached = (symbols: StockSymbol, params?: GetQuotesLogoParams): Logo | undefined =>
    this.store.getQuotesLogos(symbols, params);

  /**
   * Fetch delayed quotes data for given symbols from server
   * Uses store cache first, only fetches missing symbols
   *
   * @param {StockSymbol[]} symbols
   * @memberof QuotesManager
   */
  public getDelayedQuotes = async (symbols: StockSymbol[]): SafePromise<Record<StockSymbol, DelayedQuote>> => {
    const uniqueSymbols = this.getUniqueSymbols(symbols);
    const cachedQuotes: Record<StockSymbol, DelayedQuote> = {};

    for (const symbol of uniqueSymbols) {
      const cached = this.store.getDelayedQuote(symbol);
      if (cached) {
        cachedQuotes[symbol] = cached;
      }
    }

    const missingSymbols = uniqueSymbols.filter(symbol => !cachedQuotes[symbol]);

    if (missingSymbols.length === 0) {
      return { ok: cachedQuotes };
    }

    const fetchedQuotes = await this.delayedQuotesCoalescer.request(missingSymbols);

    if (fetchedQuotes.err) {
      return fetchedQuotes;
    }

    return { ok: { ...cachedQuotes, ...fetchedQuotes.ok } };
  };

  /**
   * Fetch ticker details for given symbols from server
   *
   * @param {StockSymbol[]} symbols
   * @memberof QuotesManager
   */
  public getTickerDetailsCache = (symbol: StockSymbol): TickerDetail | undefined => this.store.getTickerDetails(symbol);
  public getTickerDetails = async (symbols: StockSymbol[]): SafePromise<TickerDetailsResponse> => {
    const uniqueSymbols = this.getUniqueSymbols(symbols);
    const cachedBySymbol = new Map<StockSymbol, TickerDetail>();
    const missingSymbols: StockSymbol[] = [];

    uniqueSymbols.forEach(symbol => {
      const cached = this.store.getTickerDetails(symbol);
      if (cached) {
        cachedBySymbol.set(symbol, cached);
      } else {
        missingSymbols.push(symbol);
      }
    });

    if (missingSymbols.length === 0) {
      return {
        ok: {
          result: uniqueSymbols
            .map(symbol => cachedBySymbol.get(symbol))
            .filter((detail): detail is TickerDetail => Boolean(detail)),
        },
      };
    }

    const fetchedQuotes = await this.tickerDetailsCoalescer.request(missingSymbols);
    if (fetchedQuotes.err) {
      return fetchedQuotes;
    }

    fetchedQuotes.ok.result.forEach(detail => {
      cachedBySymbol.set(detail.symbol, detail);
    });

    return {
      ok: {
        result: uniqueSymbols
          .map(symbol => cachedBySymbol.get(symbol))
          .filter((detail): detail is TickerDetail => Boolean(detail)),
      },
    };
  };

  /**
   * Fetch quotes schedule from server
   *
   * @memberof QuotesManager
   */
  public getSchedule = async (): SafePromise<Schedule> => {
    return await this.request.getSchedule();
  };

  /**
   * Get detailed quotes from cache.
   *
   * If detailed quote for requested symbol is not cached, it won't be in the result
   *
   * @param {StockSymbol[]} symbols
   * @memberof QuotesManager
   */
  public getDetailedQuotesCached = (symbols: StockSymbol[]): DetailedQuotesBySymbol => {
    return symbols.reduce((acc, symbol) => {
      const detailedQuote = this.store.getDetailedQuotes(symbol);
      if (detailedQuote) {
        return { ...acc, [symbol]: detailedQuote };
      } else {
        return acc;
      }
    }, {});
  };

  /**
   * Get short interest data
   *
   * Will use cache when possible
   *
   * @param {ShortInterestParams} params
   * @memberof QuotesManager
   */
  public getShortInterest = async (params: ShortInterestParams): SafePromise<ShortInterestsDataSet> => {
    const csvSymbols = commaSeparatedSymbols(params.symbols);
    const symbols = csvSymbols
      ? csvSymbols
          .split(',')
          .map(symbol => symbol.trim())
          .filter(symbol => symbol.length > 0)
      : undefined;
    const normalizedParams: ShortInterestParams = { ...params, symbols };

    if (symbols) {
      const cached = this.store.getShortInterest(normalizedParams);
      if (cached && symbols.every(symbol => cached[symbol] !== undefined)) {
        return { ok: cached };
      }
    }

    const shortInterestQuotes = await this.request.getShortInterest(normalizedParams);
    if (shortInterestQuotes.err) {
      return shortInterestQuotes;
    }

    this.store.setShortInterest(normalizedParams, shortInterestQuotes.ok);
    this.dispatch({ quotes: shortInterestQuotes.ok, type: 'quote:short_interest_quotes' });
    return { ok: shortInterestQuotes.ok };
  };

  public addSymbolSubscription(symbol: string): DelayedQuote | undefined {
    const quote = this.feedRestful.getQuote(symbol);
    if (quote) {
      return quote;
    } else {
      this.feedRestful.addSymbolSubscription(symbol);
      return undefined;
    }
  }

  public getQuoteFromSubscriptionStore(symbol: string): DelayedQuote | undefined {
    const quote = this.feedRestful.getQuote(symbol);
    return quote;
  }

  public addSymbolsSubscription(symbols: string[]): void {
    this.feedRestful.addSymbolsSubscription(symbols);
  }

  public removeSymbolSubscription(symbol: string): void {
    this.feedRestful.removeSymbolSubscription(symbol);
  }

  public removeSymbolsSubscription(symbols: string[]): void {
    this.feedRestful.removeSymbolsSubscription(symbols);
  }

  public getBullSayBearSay = async (symbols: string[]): SafePromise<BullBearStatements[]> => {
    return await this.request.getBullSayBearSayStatements(symbols);
  };

  public getQuoteAnalysis = async (symbol: string): SafePromise<QuoteAnalysisResponse> => {
    return await this.request.getQuoteAnalysis(symbol);
  };

  public getPeerQuotesCached = (symbol: StockSymbol, limit?: number): PeerQuotesResponse | undefined => {
    return this.store.getPeerQuotes(getPeerQuotesCacheKey(symbol, limit));
  };

  public getPeerQuotes = async (symbol: StockSymbol, limit?: number): SafePromise<PeerQuotesResponse> => {
    const cacheKey = getPeerQuotesCacheKey(symbol, limit);
    const cachedPeerQuotes = this.store.getPeerQuotes(cacheKey);

    if (cachedPeerQuotes) {
      return { ok: cachedPeerQuotes };
    }

    const response = await this.request.getPeerQuotes(symbol, limit);
    if (response.ok && Object.keys(response.ok.data.peers).length > 0) {
      this.store.setPeerQuotes(cacheKey, response.ok);
    }

    return response;
  };

  protected onFirstSubscription(): void {
    this.socketSubscription = this.socket.listen(event => this.dispatch(event));
    this.requestSubscription = this.request.listen(event => this.dispatch(event));
    this.feedRestfulSubscription = this.feedRestful.listen(event => this.dispatch(event));
  }

  protected onZeroSubscriptions(): void {
    this.socketSubscription?.unsubscribe();
    this.requestSubscription?.unsubscribe();
    this.feedRestfulSubscription?.unsubscribe();
  }

  protected onSubscribe(): WatchlistQuotesFunctions {
    return {
      getDetailedQuotes: this.getDetailedQuotes,
    };
  }

  private mapQuotesLogos = (logos: Logo[]): Record<StockSymbol, Logo> => {
    return logos.reduce<Record<StockSymbol, Logo>>((acc, logo) => {
      acc[logo.search_key] = logo;
      return acc;
    }, {});
  };

  private dedupeLogosBySearchKey = (cachedLogos: Logo[], fetchedLogos: Logo[]): Logo[] => {
    const bySearchKey = new Map<StockSymbol, Logo>();
    for (const logo of cachedLogos) bySearchKey.set(logo.search_key, logo);
    for (const logo of fetchedLogos) bySearchKey.set(logo.search_key, logo);
    return [...bySearchKey.values()];
  };

  private getQuotesLogosCoalescer(
    params?: GetQuotesLogoParams,
  ): ReturnType<typeof createSymbolRequestCoalescer<QuotesLogoResult>> {
    const coalescerKey = JSON.stringify(params ?? null);
    const existing = this.quotesLogosCoalescers.get(coalescerKey);
    if (existing) {
      return existing;
    }
    const coalescer = createSymbolRequestCoalescer({
      fetch: async symbols => {
        const result = await this.fetchQuotesLogos(symbols, params);
        if (result.err || result.rejectedSymbols.length === 0) return result;
        if (result.ok.length === 0 && symbols.length > 1) {
          return { err: new SafeError(logoRequestRejectedMessage, 'response', { status: 400 }) };
        }
        this.rememberRejectedLogoSymbols(result.rejectedSymbols);
        if (typeof window === 'undefined') {
          console.warn(
            JSON.stringify({
              event: 'quotes_logos_symbols_rejected',
              fields: params?.fields ?? 'default',
              rejectedSymbols: result.rejectedSymbols,
              requestedCount: symbols.length,
              status: 400,
              timestamp: new Date().toISOString(),
            }),
          );
        }
        return result;
      },
      slice: (result, symbols) => this.sliceQuotesLogosResult(result, symbols),
    });
    this.quotesLogosCoalescers.set(coalescerKey, coalescer);
    return coalescer;
  }

  private sliceQuotesLogosResult(result: QuotesLogoResult, symbols: StockSymbol[]): QuotesLogoResult {
    if (result.err) return result;
    const wanted = new Set(symbols.map(symbol => String(symbol).toUpperCase()));
    return {
      ok: result.ok.filter(logo => wanted.has(String(logo.search_key).toUpperCase())),
      rejectedSymbols: result.rejectedSymbols.filter(symbol => wanted.has(String(symbol).toUpperCase())),
    };
  }

  private sliceDelayedQuotesResult(
    result: SafeType<Record<StockSymbol, DelayedQuote>>,
    symbols: StockSymbol[],
  ): SafeType<Record<StockSymbol, DelayedQuote>> {
    if (result.err) return result;

    const quotesBySymbol = result.ok ?? {};
    const byUpper = new Map(Object.entries(quotesBySymbol).map(([key, quote]) => [key.toUpperCase(), quote] as const));
    const sliced: Record<StockSymbol, DelayedQuote> = {};
    for (const symbol of symbols) {
      const quote = quotesBySymbol[symbol] ?? byUpper.get(String(symbol).toUpperCase());
      if (quote) sliced[symbol] = quote;
    }
    return { ok: sliced };
  }

  private sliceTickerDetailsResult(
    result: SafeType<TickerDetailsResponse>,
    symbols: StockSymbol[],
  ): SafeType<TickerDetailsResponse> {
    if (result.err) return result;
    const wanted = new Set(symbols.map(symbol => String(symbol).toUpperCase()));
    return {
      ok: {
        result: (result.ok?.result ?? []).filter(detail => wanted.has(String(detail.symbol).toUpperCase())),
      },
    };
  }

  private async fetchTickerDetails(symbols: StockSymbol[]): SafePromise<TickerDetailsResponse> {
    const shortInterestQuotes = await this.request.getTickerDetails(symbols);
    if (shortInterestQuotes.err) {
      return shortInterestQuotes;
    }

    shortInterestQuotes.ok.result.forEach(detail => this.store.setTickerDetails(detail));
    this.dispatch({
      detail: shortInterestQuotes.ok.result,
      type: 'quote:ticker_details_quotes',
    });
    return shortInterestQuotes;
  }

  private async fetchDelayedQuotes(symbols: StockSymbol[]): ReturnType<WatchlistQuotesRequest['getDelayedQuotes']> {
    try {
      const result = await this.request.getDelayedQuotes(symbols);
      if (result.err) {
        return result;
      }
      const quotes = result.ok;
      if (quotes == null || typeof quotes !== 'object') {
        return {
          err: new SafeError('delayed quotes response missing ok payload', 'invalid_response'),
        };
      }
      for (const [symbol, quote] of Object.entries(quotes)) {
        this.store.setDelayedQuotes(symbol, quote);
      }
      return result;
    } catch (cause: unknown) {
      return {
        err: new SafeError('delayed quotes request or processing failed', 'request_processing', cause),
      };
    }
  }

  private async fetchQuotesLogos(symbols: StockSymbol[], params?: GetQuotesLogoParams): Promise<QuotesLogoResult> {
    try {
      const response = await this.request.getQuotesLogos(symbols, params);
      if (!response.err) {
        const payload = response.ok;
        if (payload == null || typeof payload !== 'object') {
          return { err: new SafeError('quotes logos response missing ok payload', 'invalid_response') };
        }
        const logos = Array.isArray(payload.data) ? payload.data : [];
        for (const logo of logos) {
          this.store.addQuotesLogos(logo.search_key, params, logo);
        }
        return { ok: logos, rejectedSymbols: [] };
      }

      const status = readLogoHttpStatus(response.err);
      if (status !== 400) return response;
      if (symbols.length <= 1) return { ok: [], rejectedSymbols: [...symbols] };

      const middle = Math.ceil(symbols.length / 2);
      const [left, right] = await Promise.all([
        this.fetchQuotesLogos(symbols.slice(0, middle), params),
        this.fetchQuotesLogos(symbols.slice(middle), params),
      ]);
      if (left.err) return left;
      if (right.err) return right;
      return {
        ok: [...left.ok, ...right.ok],
        rejectedSymbols: [...left.rejectedSymbols, ...right.rejectedSymbols],
      };
    } catch (cause: unknown) {
      return { err: new SafeError('quotes logos request or processing failed', 'request_processing', cause) };
    }
  }

  private partitionRejectedLogoSymbols(symbols: StockSymbol[]) {
    const now = Date.now();
    const freshSymbols: StockSymbol[] = [];
    const rejectedSymbols: StockSymbol[] = [];
    for (const symbol of symbols) {
      const key = String(symbol).toUpperCase();
      const expiresAt = this.rejectedLogoSymbols.get(key);
      if (expiresAt !== undefined && expiresAt > now) {
        rejectedSymbols.push(symbol);
      } else {
        if (expiresAt !== undefined) this.rejectedLogoSymbols.delete(key);
        freshSymbols.push(symbol);
      }
    }
    return { freshSymbols, rejectedSymbols };
  }

  private rememberRejectedLogoSymbols(symbols: StockSymbol[]) {
    const expiresAt = Date.now() + REJECTED_LOGO_TTL_MS;
    for (const symbol of symbols) {
      this.rejectedLogoSymbols.set(String(symbol).toUpperCase(), expiresAt);
    }
  }

  private getUniqueMissingLogoSymbols(symbols: StockSymbol[], params?: GetQuotesLogoParams): StockSymbol[] {
    return symbols.filter(symbol => !this.store.getQuotesLogos(symbol, params));
  }

  private getUniqueSymbols(symbols: StockSymbol[]): StockSymbol[] {
    return [...new Set(symbols)];
  }
}
